import { useCallback, useEffect, useRef } from 'react';
import {
  Controller,
  UseFormReturn,
  useFieldArray,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Divider } from 'components/layout';
import { Typography } from 'components/typography/Typography';
import {
  ALJEX_CHARACTER_LIMITS,
  truncateCommodityDescription,
} from 'pages/QuoteView/LoadBuilding/AljexSectionForms/characterLimits';
import { transportTypes } from 'pages/QuoteView/LoadBuilding/AljexSectionForms/constants';
import { extractAljexFootageFromTransportSize } from 'pages/QuoteView/LoadBuilding/AljexSectionForms/utils';
import {
  EQUIPMENT_PLACEHOLDER,
  EQUIPMENT_REQUIRED_MESSAGE,
  validateEquipmentRequired,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { Commodity, NormalizedLoad, createNewCommodity } from 'types/Load';
import { Undef } from 'types/UtilityTypes';
import { validateRequiredSelectMembership } from 'utils/formValidation';
import { objectHasTruthyValues } from 'utils/isValidObject';

import { LoadBuildingTextInput } from './UnifiedAljexLoadForm';
import { CommodityItemInputCard } from './components/CommodityItemInputCard';

const SpecificationsForm = ({
  isCreateMode,
  formMethods,
  initialTransportType,
}: {
  isCreateMode: boolean;
  formMethods: UseFormReturn<NormalizedLoad>;
  initialTransportType?: string;
}) => {
  const { setValue, watch, control, formState } = formMethods;
  const { errors } = formState;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'commodities',
  });

  const hasManuallyEditedDescriptions = useRef(isCreateMode ? false : true);

  const hasInteractedWithCommodities = useRef(false);

  // Track if user has manually edited the commodities description
  useEffect(() => {
    if (formState.dirtyFields.specifications?.commodities) {
      hasManuallyEditedDescriptions.current = true;
    }
  }, [formState.dirtyFields.specifications?.commodities]);

  const removeCommodity = (index: number) => {
    remove(index);
  };

  const addCommodity = () => append(createNewCommodity());

  // Update auto-calculated form fields when commodities change; useWatch is more reliable than watch for deep watching of form arrays
  const watchedCommodities = useWatch({
    control,
    name: 'commodities',
  });
  const watchedTransportSize = useWatch({
    control,
    name: 'specifications.transportSize',
  });
  const watchedTransportType = useWatch({
    control,
    name: 'specifications.transportType',
  });
  const parsedFootageForDisplay = extractAljexFootageFromTransportSize(
    String(watchedTransportSize || '').trim()
  );
  const hasNumericFootage = !!parsedFootageForDisplay;
  const normalizedInitialTransportType = String(
    initialTransportType || ''
  ).trim();
  // When normalization left no baseline type, comparing to "" made every real
  // selection differ and hid footage (flicker). Footage comes from transportSize only;
  // tie visibility to equipment type only when we have a non-empty initial type.
  const equipmentTypeMatchesInitialForFootage =
    normalizedInitialTransportType === '' ||
    String(watchedTransportType || '').trim() ===
      normalizedInitialTransportType;
  const shouldShowFootage =
    hasNumericFootage && equipmentTypeMatchesInitialForFootage;

  const updateFormFields = useCallback(() => {
    const commodities = watchedCommodities || [];

    // Check if any commodity has actual data (weight or quantity)
    const hasActualData = commodities.some((item: Commodity) =>
      objectHasTruthyValues(item, ['weightTotal', 'quantity'])
    );

    // Once user adds commodities with data or there has always been commodities,
    // mark as interacted so totals will now track commodities
    if (hasActualData) {
      hasInteractedWithCommodities.current = true;
    }

    // Calculates totals based off of commodities data
    // Will not run if there are no commodities (displaying ai suggested value if there are any)
    if (hasInteractedWithCommodities.current) {
      const totalQuantity = commodities.reduce(
        (sum: number, item: Commodity) => sum + (item.quantity || 0),
        0
      );
      const totalWeight = commodities.reduce(
        (sum: number, item: Commodity) => sum + (item.weightTotal || 0),
        0
      );

      setValue('specifications.totalPieces.val', totalQuantity);
      setValue('specifications.totalWeight.val', totalWeight);
    }

    // Only update descriptions if user hasn't manually edited them
    // and the descriptions are empty
    const specificationsCommodities = watch('specifications.commodities');
    if (
      !hasManuallyEditedDescriptions.current &&
      !specificationsCommodities?.trim()
    ) {
      let descriptions = commodities
        .map((item: Commodity) => item.description)
        .filter((desc: string) => desc?.trim())
        .join(', ');

      // Truncate if exceeds character limit
      descriptions = truncateCommodityDescription(descriptions);

      setValue('specifications.commodities', descriptions);
    }
  }, [watchedCommodities, setValue, watch]);

  useEffect(() => {
    updateFormFields();
  }, [updateFormFields]);

  return (
    <div
      className='grid grid-cols-4 gap-2 mx-0 w-full'
      data-testid='specifications-form'
    >
      <div className='col-span-4'>
        <Label name={'specifications.transportType'} required={true}>
          Equipment Type
        </Label>
        <Controller
          name='specifications.transportType'
          control={control}
          rules={{
            validate: (value) => {
              if (!isCreateMode) {
                return validateEquipmentRequired(value);
              }
              return validateRequiredSelectMembership(
                value,
                (transportTypes ?? []).map((type) => String(type.value)),
                { message: EQUIPMENT_REQUIRED_MESSAGE }
              );
            },
          }}
          render={({ field }) => {
            const optionValues = new Set(
              (transportTypes ?? []).map((type) => String(type.value))
            );
            const selected = String(field.value ?? '').trim();
            let selectValue: Undef<string>;
            if (selected) {
              if (!isCreateMode || optionValues.has(selected)) {
                selectValue = selected;
              }
            }
            return (
              <Select
                showSearch
                className='text-neutral-600'
                placeholder={EQUIPMENT_PLACEHOLDER}
                optionFilterProp='children'
                onChange={field.onChange}
                value={selectValue}
                options={transportTypes?.map((transportType) => ({
                  value: transportType.value,
                  label: transportType.label,
                }))}
                data-testid='specifications-equipment-type-select'
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name='specifications.transportType'
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500 mt-1'>
              {message}
            </Typography>
          )}
        />
      </div>

      <div className='col-span-4'>
        <RHFTextInput
          name='specifications.commodities'
          label='Description'
          required={true}
          placeholder='Enter description'
          maxLength={ALJEX_CHARACTER_LIMITS['specifications.commodities']}
          data-testid='specifications-commodities-description-input'
        />
      </div>

      {shouldShowFootage && (
        <div
          className='col-span-4'
          data-testid='specifications-footage-display'
        >
          <Label name='specifications-footage-display'>Footage</Label>
          <Typography
            variant='body-sm'
            className='mt-1 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-neutral-800'
          >
            {parsedFootageForDisplay}
          </Typography>
        </div>
      )}

      <Divider className='col-span-4' />
      <Typography variant='h5' className='col-span-4'>
        Items
      </Typography>
      {fields.length > 0 && (
        <div
          className='flex flex-col gap-4 col-span-4'
          data-testid='specifications-commodities-container'
        >
          {fields.map((field, index) => (
            <CommodityItemInputCard
              formMethods={formMethods}
              key={field.id}
              item={watch(`commodities.${index}`) || field}
              index={index}
              onRemove={() => removeCommodity(index)}
              onSave={updateFormFields}
            />
          ))}
        </div>
      )}

      <div className='col-span-4 flex items-center justify-end mr-px'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          onClick={addCommodity}
          className='h-8 px-3 text-xs text-brand-500 hover:-mx-px'
          buttonNamePosthog={null}
          data-testid='specifications-add-commodity-button'
        >
          + Add Commodity
        </Button>
      </div>

      <div
        className='col-span-4 mt-2'
        data-testid='specifications-load-summary'
      >
        <div className='text-sm font-medium text-neutral-700 mb-4'>
          Load Summary (Auto-calculated)
        </div>
        <div className='grid grid-cols-4 gap-4 mx-0 w-full'>
          <div className='col-span-2'>
            <LoadBuildingTextInput
              name='specifications.totalPieces.val'
              label='Total Quantity'
              inputType='number'
              options={{ valueAsNumber: true }}
              required={false}
              readOnly={true}
              data-testid='specifications-total-pieces-input'
            />
          </div>
          <div className='col-span-2'>
            <LoadBuildingTextInput
              name='specifications.totalWeight.val'
              label='Total Weight'
              inputType='number'
              options={{ valueAsNumber: true }}
              required={false}
              readOnly={true}
              data-testid='specifications-total-weight-input'
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default SpecificationsForm;
